import { TransformedFilterTreeNode } from 'components/tree/FilterTree';
import { CountryCodeType } from './country';

//
// --- Types ---

export type SearchFilterNameKeyType = keyof typeof SEARCH_FILTER_NAME;
export type SearchFilterNameValueType = typeof SEARCH_FILTER_NAME[SearchFilterNameKeyType];

export type SupplierActivityFilterTreeNodeIdKey = keyof typeof SUPPLIER_ACTIVITY_FILTER_TREE_NODE_ID;
export type SupplierActivityFilterTreeNodeId =
  typeof SUPPLIER_ACTIVITY_FILTER_TREE_NODE_ID[SupplierActivityFilterTreeNodeIdKey];

interface GeographicRegionType {
  /** Gotcha: `id` is more like a "name" than an "id", but this is what the api returns */
  id: string;
  /** Gotcha: `label` is more like an "id". Its a 3 letter code for the country. This is what the api returns */
  label: CountryCodeType;
  name: string;
}

export interface CountryType extends GeographicRegionType {
  children?: ReadonlyArray<StateType>;
}

export interface StateType extends GeographicRegionType {}

//
// --- Search Filter Constants ---

export const SEARCH_FILTER_NAME = {
  ACTIVE_USER: 'active-user-filter',
  COUNTRY_OR_STATE: 'country-or-state-filter',
  DIVERSITY_CLASSIFICATION: 'diversity-classification-filter',
  DIVERSITY_STATUS: 'diversity-status-filter',
  INVITED_EVENT_COUNT: 'invited-event-count-filter',
  INVITED_PORTAL_COUNT: 'invited-portal-count-filter',
  ITEM: 'item-filter',
  PARENT_COMPANY_PERSON: 'parent-company-person-filter',
  PERSON_NAME: 'name-filter',
  POWERSCORE: 'powerscore-filter',
  QUALIFICATION: 'qualification-filter',
  SEGMENTATION: 'segmentation-filter',
  SOURCE: 'source-filter'
} as const;

export const CompanySearchFilterNames: ReadonlyArray<SearchFilterNameValueType> = [
  SEARCH_FILTER_NAME.COUNTRY_OR_STATE,
  SEARCH_FILTER_NAME.DIVERSITY_CLASSIFICATION,
  SEARCH_FILTER_NAME.DIVERSITY_STATUS,
  SEARCH_FILTER_NAME.INVITED_EVENT_COUNT,
  SEARCH_FILTER_NAME.INVITED_PORTAL_COUNT,
  SEARCH_FILTER_NAME.ITEM,
  SEARCH_FILTER_NAME.PARENT_COMPANY_PERSON,
  SEARCH_FILTER_NAME.QUALIFICATION,
  SEARCH_FILTER_NAME.SEGMENTATION,
  SEARCH_FILTER_NAME.SOURCE
];

export const ContactSearchFilterNames: ReadonlyArray<SearchFilterNameValueType> = [
  SEARCH_FILTER_NAME.ACTIVE_USER,
  SEARCH_FILTER_NAME.PERSON_NAME,
  SEARCH_FILTER_NAME.POWERSCORE
];

//
// --- Filter Tree Constants ---

export type FilterTreeTypeKey = keyof typeof FILTER_TREE_TYPE;
export type FilterTreeType = typeof FILTER_TREE_TYPE[FilterTreeTypeKey];

export const FILTER_TREE_TYPE = {
  CONTACT: 'filter-tree-contact',
  COUNTRY_OR_STATE: 'filter-tree-country-or-state',
  DIVERSE: 'filter-tree-diverse',
  DIVERSITY_CLASSIFICATION: 'filter-tree-diversity-classification',
  INVITED_EVENT_COUNT: 'filter-tree-invited-event-count',
  INVITED_PORTAL_COUNT: 'filter-tree-invited-portal-count',
  ITEM: 'filter-tree-item',
  QUALIFICATION: 'filter-tree-qualification',
  SEGMENTATION: 'filter-tree-segmentation',
  SOURCE: 'filter-tree-source'
} as const;

//
// --- Country State Filter Constants ---

export const COUNTRY_FILTER_ID_PREFIX = 'country-state-filter';

export const COUNTRY_FILTER_TREE_NODE_ID = {
  ROOT: `${COUNTRY_FILTER_ID_PREFIX}-root`,
  OTHER: `${COUNTRY_FILTER_ID_PREFIX}-other`
};

export const baseGeographicRegionTreeNode: Omit<TransformedFilterTreeNode, 'children' | 'id' | 'name' | 'parentId'> = {
  checked: false,
  filterTreeType: FILTER_TREE_TYPE.COUNTRY_OR_STATE,
  leaf: false,
  open: false
};

/** top-level node for country filter options */
export const baseCountryFilterTreeNode: Omit<TransformedFilterTreeNode, 'children'> = {
  ...baseGeographicRegionTreeNode,
  checked: null,
  id: COUNTRY_FILTER_TREE_NODE_ID.ROOT,
  name: 'Location',
  parentId: null
};

export const baseOtherCountryTreeNode: Omit<TransformedFilterTreeNode, 'children'> = {
  ...baseGeographicRegionTreeNode,
  checked: null,
  id: COUNTRY_FILTER_TREE_NODE_ID.OTHER,
  leaf: true,
  name: 'Other Countries',
  parentId: COUNTRY_FILTER_TREE_NODE_ID.ROOT
};

//
// --- Qualification Filter Constants ---

export const DIVERSITY_QUALIFICATION_FILTER_TREE_NODE_ID = {
  APPROVED: '1:1'
};

//
// --- Invited Event Filter Constants ---

export const SUPPLIER_ACTIVITY_FILTER_ID_PREFIX = 'supplier-activity-filter';

export const SUPPLIER_ACTIVITY_FILTER_TREE_NODE_ID = {
  INVITED_EVENT_COUNT: `${SUPPLIER_ACTIVITY_FILTER_ID_PREFIX}-invited-event-count`,
  INVITED_EVENT_RANGE: `${SUPPLIER_ACTIVITY_FILTER_ID_PREFIX}-invited-event-range`,
  INVITED_PARENT_COUNT: `${SUPPLIER_ACTIVITY_FILTER_ID_PREFIX}-parent`,
  INVITED_PORTAL_COUNT: `${SUPPLIER_ACTIVITY_FILTER_ID_PREFIX}-invited-portal-count`,
  INVITED_PORTAL_RANGE: `${SUPPLIER_ACTIVITY_FILTER_ID_PREFIX}-invited-portal-range`
} as const;

export const INVITED_EVENT_COUNT_MAX = 15;

// range nodes

export const supplierActivityFilterInvitedEventTreeNode: TransformedFilterTreeNode = {
  checked: null,
  children: [],
  filterTreeType: FILTER_TREE_TYPE.INVITED_EVENT_COUNT,
  id: SUPPLIER_ACTIVITY_FILTER_TREE_NODE_ID.INVITED_EVENT_RANGE,
  leaf: true,
  name: 'Range',
  open: false,
  parentId: SUPPLIER_ACTIVITY_FILTER_TREE_NODE_ID.INVITED_EVENT_COUNT
};

export const supplierActivityFilterInvitedPortalTreeNode: TransformedFilterTreeNode = {
  ...supplierActivityFilterInvitedEventTreeNode,
  filterTreeType: FILTER_TREE_TYPE.INVITED_PORTAL_COUNT,
  id: SUPPLIER_ACTIVITY_FILTER_TREE_NODE_ID.INVITED_PORTAL_RANGE,
  parentId: SUPPLIER_ACTIVITY_FILTER_TREE_NODE_ID.INVITED_PORTAL_COUNT
};

// parent nodes

export const supplierActivityFilterInvitedEventParentTreeNode: TransformedFilterTreeNode = {
  checked: null,
  filterTreeType: FILTER_TREE_TYPE.INVITED_EVENT_COUNT,
  id: SUPPLIER_ACTIVITY_FILTER_TREE_NODE_ID.INVITED_EVENT_COUNT,
  leaf: false,
  name: 'Events Invited',
  open: true,
  parentId: 'activity-filter-parent',
  tooltip: 'Aggregate of all event participation in Wood Mackenzie Supply Chain Sourcing Intelligence',
  children: [supplierActivityFilterInvitedEventTreeNode]
};

export const supplierActivityFilterInvitedPortalParentTreeNode: TransformedFilterTreeNode = {
  ...supplierActivityFilterInvitedEventParentTreeNode,
  filterTreeType: FILTER_TREE_TYPE.INVITED_PORTAL_COUNT,
  id: SUPPLIER_ACTIVITY_FILTER_TREE_NODE_ID.INVITED_PORTAL_COUNT,
  name: 'Portals Invited',
  tooltip: 'Aggregate of all portal participation in Wood Mackenzie Supply Chain Supplier Intelligence',
  children: [supplierActivityFilterInvitedPortalTreeNode]
};

// root node
export const supplierActivityFilterRootTreeNode: TransformedFilterTreeNode = {
  checked: null,
  filterTreeType: FILTER_TREE_TYPE.INVITED_EVENT_COUNT,
  id: SUPPLIER_ACTIVITY_FILTER_TREE_NODE_ID.INVITED_PARENT_COUNT,
  leaf: false,
  name: 'Supplier Activity',
  open: true,
  parentId: null,
  children: [supplierActivityFilterInvitedEventParentTreeNode, supplierActivityFilterInvitedPortalParentTreeNode]
};

//
// --- Source Filter Constants ---

export const SOURCE_FILTER_ID_PREFIX = 'source-filter';

export const SOURCE_FILTER_TREE_NODE_ID = {
  ROOT: `${SOURCE_FILTER_ID_PREFIX}-root`,
  REGISTERED_SUPPLIER: `${SOURCE_FILTER_ID_PREFIX}-registered-supplier`
};

export const sourceFilterRegisteredSupplierTreeNode: TransformedFilterTreeNode = {
  checked: false,
  children: [],
  filterTreeType: FILTER_TREE_TYPE.SOURCE,
  id: SOURCE_FILTER_TREE_NODE_ID.REGISTERED_SUPPLIER,
  leaf: true,
  name: 'Branded Registration',
  open: false,
  parentId: SOURCE_FILTER_TREE_NODE_ID.ROOT
};

export const sourceFilterRootTreeNode: TransformedFilterTreeNode = {
  checked: null,
  children: [{ ...sourceFilterRegisteredSupplierTreeNode }],
  filterTreeType: FILTER_TREE_TYPE.SOURCE,
  id: SOURCE_FILTER_TREE_NODE_ID.ROOT,
  leaf: false,
  name: 'Source',
  open: false,
  parentId: null
};
